/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {useEffect} from "react";
import LayoutFullWidth from "@/app/components/layouts/LayoutFullWidth";
import classes from "/styles/cmsPages/commonCmsPagesStyles.module.scss";
import useGetCmsPageData from "@/app/connector/pages/getCmsPageData";
import {TCmsPageAllStoresData} from "@/app/types/prefetchedData/cmsPageData";
import {useRouter} from "next/router";
import NoRoute from "@/app/components/NoRoute";
import getCurrentStoreCmsPageData from "@/app/connector/pages/getCunrrentStoreCmsPageData";
import {useAppSelector} from "@/app/hooks/redux";
import {TRouteInfo} from "@/app/types/prefetchedData/routeInfo";
import {triggerEvent} from "@/app/utils/statistic";
import {STATISTIC_EVENTS} from "@/app/constants";

export default function CmsPageView(props: {
    cmsPageData: TCmsPageAllStoresData,
    routeInfo: TRouteInfo,
}) {
    const router = useRouter();
    const { url } = router.query;
    const urlPath = url ? (url as Array<string>).join('/') : '';
    const currentStorePageData = getCurrentStoreCmsPageData(props.cmsPageData)
    const {reloadRequired} = useAppSelector(state => state.storeConfigReducer);
    const {cmsPageContent, cmsPageData, notFound, loading, error} = useGetCmsPageData({
        prefetchedCmsPageData: currentStorePageData,
        identifier: props.routeInfo?.identifier,
    })

    useEffect(() => {
        // Trigger event to be able to send statistic data
        triggerEvent({
            type: STATISTIC_EVENTS.pageView,
            details: {
                url: urlPath,
                pageData: cmsPageData,
            }
        });
    }, [urlPath]);

    if(notFound) {
        return <NoRoute loading={reloadRequired} />;
    }

    const metaTitle = cmsPageData.meta_title ?? cmsPageData.title ?? '';
    const metaDescription = cmsPageData.meta_description ?? '';

    const pageLayout = cmsPageData?.page_layout ?? '';

    switch (pageLayout) {
        // Here you can add custom case for particular CMS page layout
        default:
            return <LayoutFullWidth
                headTitle={metaTitle}
                metaDescription={metaDescription ?? metaTitle}
                canonical={props.routeInfo.relative_url ?? undefined}
                mainContainerClassName={`${classes['cms-page']} ${urlPath}`}
            >
                {cmsPageContent}
            </LayoutFullWidth>;
    }
}